import { CommonModule } from '@angular/common';
import { Component, inject, Input, signal } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { differenceInCalendarDays } from 'date-fns';
import { StoreyWithoutMeterService } from '../../../../../../apps/mobile/src/app/services/storey-without-meter.service';
import { getDefaultDateForInhibitionTimeout } from '../../../../../utils-common/src';
import { DeviceData3D } from '../../models/device-data.model';
import { BatteryState } from '../../models/enums/battery-state.enum';
import { DeviceState } from '../../models/enums/device-state.enum';
import { DeviceType3D, DeviceTypeEnum, PanelType } from '../../models/enums/device-type.enum';
import { BatterieIconComponent } from '../batterie-icon/batterie-icon.component';
import { DeviceTypeLabelComponent } from '../device-type-label/device-type-label.component';
import { EnergyInfoComponent } from '../energy-info/energy-info.component';
import { FadingSpanComponent } from '../fading-span/fading-span.component';

@Component({
    selector: 'web-card-compact',
    templateUrl: './card-compact.component.html',
    styleUrls: ['./card-compact.component.scss'],
    imports: [
        CommonModule,
        TranslateModule,
        BatterieIconComponent,
        EnergyInfoComponent,
        DeviceTypeLabelComponent,
        FadingSpanComponent,
        IonicModule,
    ],
})
export class CardCompactComponent {
    @Input()
    device!: DeviceData3D;

    public DeviceType3D = DeviceType3D;
    public DeviceTypeEnum = DeviceTypeEnum;

    public DeviceState = DeviceState;

    public PanelType = PanelType;

    public BatteryState = BatteryState;

    private storeyWithoutMeterService: StoreyWithoutMeterService = inject(StoreyWithoutMeterService);

    public get isMeter(): boolean {
        return this.device.type === DeviceType3D.METER;
    }

    public hasConsumption(): boolean {
        return (
            this.device.name === 'HOME' &&
            !!(this.device.hasLoadPhase || this.device.hasConsumptionDevice || this.device.consumptionPerDevice?.length)
        );
    }

    public showBattery(): boolean {
        return this.device.panelType === PanelType.PLAY_MAX && this.device.state === DeviceState.CONNECTED;
    }

    protected getDaysDiffToDisplay(): string {
        const daysCount = differenceInCalendarDays(
            this.device.inhibitionTimeout ?? getDefaultDateForInhibitionTimeout(),
            new Date(),
        );
        return daysCount > 0 ? '+' + daysCount : '';
    }

    protected isBatteryPaused(): boolean {
        if (this.device.type === DeviceType3D.STOREY) {
            return this.getStoreyInhibitionDate() > new Date();
        }

        return false;
    }

    /*
     * Use StoreyWithoutMeterService to get up to date inhibitionTimeout. Retrieving it from devices or
     * deviceList in overview component may result in reading stale inhibitionTimeout after the pause
     * has been set in parameters, which can prevent from correctly displaying that the battery is
     * currently paused.
     */
    protected getStoreyInhibitionDate(): Date {
        if (this.device.type === DeviceType3D.STOREY) {
            return new Date(
                this.storeyWithoutMeterService.storey()?.find(s => s.serialNumber === this.device.id)
                    ?.inhibitionTimeout ?? getDefaultDateForInhibitionTimeout(),
            );
        }
        return new Date();
    }

    protected showDetail = signal(false);

    protected toggleDetail(): void {
        this.showDetail.update(v => !v);
    }

    public openDetail(): void {
        this.showDetail.set(true);
    }
}
